import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { OverlayTrigger, Popover, Container } from 'react-bootstrap';
import { isEmpty } from 'lodash';
import { useDispatch } from 'react-redux';
import Loader from '../../../../Components/Utility/Loader/Loader';
import ProductPurchaseOrderHeader from './Components/ProductPurchaseOrderHeader';
import ProductPurchaseOrderTable from './Components/ProductPurchaseOrderTable';
import CustomTooltip from '../../../../Components/Tooltip/CustomTooltip';
import { toasterMsg } from '../../../../Components/Toaster/Toaster';
import ImportModal from '../../../Library/BomLibrary/ImportModal';
import ErrorModal from '../../../Library/BomLibrary/ErrorModal';
import { useProductPurchaseOrderTodo } from './hooks/useProductPurchaseOrderTodo';
import { useProductPurchaseOrderLogic } from './hooks/useProductPurchaseOrderLogic';
import {
  downloadPOSampleSheet,
  downloadSelectedPurchaseOrderTodos,
  downloadAllPurchaseOrderTodos,
  getPresignedUrlForPOUpload,
  uploadPOFileToS3,
} from "./store/productPurchaseOrderTodo.action";
import { getPreReadSignedWriteUrl } from "../../../../services/s3Services";
import "./ProductPurchaseOrderTodo.css";

function ProductPurchaseOrderTodo() {
  const { t } = useTranslation('purchase-order');
  const history = useHistory();
  const dispatch = useDispatch();
  const expandSearchRef = useRef(null);
  const initialLoadCompleted = useRef(false);
  const docRef = useRef(null);
  const [actionState, setActionState] = useState({
    downloadLoading: false,
    showUploadModal: false,
    showErrModal: false,
    importLoading: false,
    filesSelected: null,
    totalRecordsToUpload: 0,
    errorRecordCount: 0,
    successRecordCount: 0,
    errFile: '',
  });

  const hookData = useProductPurchaseOrderTodo();
  const {
    state,
    setState,
    searchState,
    setSearchState,
    filterState,
    setFilterState,
    isSupplierLoggedIn,
    userInfo,
    logInRights,
    allTierList,
    ColorLibList,
    lengthVisible,
    totalCount,
    error,
    loading,
    fetchPurchaseOrderTodos,
    debouncedSave,
    debounceTimeoutRef,
    currentlyProcessingRef,
    pendingUpdatesRef,
    fetchRef,
  } = hookData;

  const logic = useProductPurchaseOrderLogic({
    state,
    setState,
    searchState,
    setSearchState,
    filterState,
    fetchPurchaseOrderTodos,
    debouncedSave,
    debounceTimeoutRef,
    currentlyProcessingRef,
    pendingUpdatesRef,
    isSupplierLoggedIn,
    userInfo,
    logInRights,
    lengthVisible,
    ...hookData,
  });

  const {
    currencySymbol,
    columnDropdownOptions,
    selectedColumns,
    totalVisibleColumns,
    isColVisible,
    isRowEditable,
    isColumnEditable,
    handleSelectedColumns,
    handleSearch,
    handleClearSearch,
    handleSelectAllPO,
    handleExpandAllFalsePO,
    handleExpandAllTruePO,
    handleSelectPO,
    expandPO,
    handleRevisedColumns,
    handleActualColumns,
    handleAutoSave,
    handlePageNextInScroll,
    handleApplyFilter,
  } = logic;

  const downloading = 'COMMON_LABEL.DOWNLOADING';

  // Helper functions for table rendering
  const renderGroupHeaders = (groupKey, labels, classes = {}, options = {}) => {
    const headers = [];
    headers.push(
      <th key={`${groupKey}-original`} className={classes?.original || 'w-120px'}>
        {labels.original}
      </th>,
    );
    if (state.showRevisedColumns) {
      headers.push(
        <th key={`${groupKey}-revised`} className={`${classes?.revised || 'w-120px'} revised-column bg-light-grey`}>
          {labels.revised}
        </th>,
      );
    }
    if (state.showActualColumns && options?.hasActual) {
      headers.push(
        <th key={`${groupKey}-actual`} className={`${classes?.actual || 'w-120px'} bg-dark-grey`}>
          {labels.actual}
        </th>,
      );
    }
    return headers;
  };

  const renderGroupCells = (order, groupKey, getters, classes = {}, options = {}) => {
    const cells = [];
    cells.push(
      <td key={`${order._id}-${groupKey}-original`} className={classes?.original || ''}>
        {getters?.original ? getters.original(order) ?? '-' : '-'}
      </td>,
    );
    if (state.showRevisedColumns) {
      cells.push(
        <td key={`${order._id}-${groupKey}-revised`} className={`revised-column non-editable bg-light-grey ${classes?.revised || ''}`.trim()}>
          {getters?.revised ? getters.revised(order) ?? '-' : '-'}
        </td>,
      );
    }
    if (state.showActualColumns && options?.hasActual) {
      cells.push(
        <td key={`${order._id}-${groupKey}-actual`} className={`bg-dark-grey ${classes?.actual || ''}`.trim()}>
          {getters?.actual ? getters.actual(order) ?? '-' : '-'}
        </td>,
      );
    }
    return cells;
  };

  const handleIconClick = () => {
    expandSearchRef.current?.classList.toggle('active-input');
  };

  const downloadExcelSheet = async (type) => {
    setActionState((prev) => ({ ...prev, downloadLoading: true }));

    try {
      if (type === 'All') {
        const params = {
          searchText: searchState?.searchText,
          filters: {
            supplierIds: filterState?.activeSupplier || [],
            colourwayIds: filterState?.activeColourways || [],
            statuses: filterState?.activeStatus || [],
          },
        };
        await dispatch(downloadAllPurchaseOrderTodos(params, t));
      } else if (type === 'Sample') {
        const params = {
          searchText: searchState?.searchText,
          filters: {
            supplierIds: filterState?.activeSupplier || [],
            colourwayIds: filterState?.activeColourways || [],
            statuses: filterState?.activeStatus || [],
          },
        };
        await dispatch(downloadPOSampleSheet(params, t));
      } else if (type === 'selected') {
        await dispatch(downloadSelectedPurchaseOrderTodos(state?.selectedIds, t));
      }
    } catch (error) {
      console.error('Error in downloadExcelSheet:', error);
    } finally {
      setActionState((prev) => ({ ...prev, downloadLoading: false }));
    }
  };

  const renderDownloadPopover = () => {
    return (
      <ul>
        <li>
          <button className="p-0 btn" type="button" onClick={() => downloadExcelSheet('Sample')} disabled={actionState?.downloadLoading}>
            {actionState?.downloadLoading ? t(downloading) : t('COMMON_LABEL.DOWNLOAD_SAMPLE_FILE')}
          </button>
        </li>
        <li>
          <button className="p-0 btn" type="button" disabled={isEmpty(state?.selectedIds)} onClick={() => downloadExcelSheet('selected')}>
            {actionState.downloadLoading ? t(downloading) : t('COMMON_LABEL.DOWNLOAD_SELECTED_DATA')}
          </button>
        </li>
        <li>
          <button
            type="button"
            disabled={!state?.purchaseOrderTodos?.length}
            onClick={() => downloadExcelSheet('All')}
            className={actionState.downloadLoading ? 'disabled-btn p-0 btn' : 'p-0 btn'}
          >
            {actionState.downloadLoading ? t(downloading) : t('COMMON_LABEL.DOWNLOAD_ALL_DATA')}
          </button>
        </li>
      </ul>
    );
  };

  const uploadXlsx = () => {
    setActionState((prevState) => ({
      ...prevState,
      showUploadModal: true,
    }));
  };

  const closeUploadModal = () => {
    setActionState((prevState) => ({
      ...prevState,
      showUploadModal: false,
      importLoading: false,
      filesSelected: null,
      showErrModal: false,
      totalRecordsToUpload: 0,
      errorRecordCount: 0,
      successRecordCount: 0,
      errFile: '',
    }));
  };

  const handleFile = async () => {
    if (docRef?.current) {
      const uploadedDoc = await docRef.current.selectedDocument();

      if (!uploadedDoc) return;

      const binary = uploadedDoc.document;
      binary.fileName = binary.name;

      setActionState((prev) => ({ ...prev, filesSelected: binary, importLoading: true }));

      if (binary.size > 26214400) {
        toasterMsg('File size too large', 'danger');
        return;
      }
      closeUploadModal();
    }
  };

  const toggleErr = () => {
    setActionState((prev) => ({ ...prev, showErrModal: false }));
  };

  const handleErrorDownload = async () => {
    const errFile = actionState?.errFile;
    if (!errFile) return;
    let href;
    if (errFile.startsWith('http')) {
      href = errFile;
    } else if (errFile.startsWith('/')) {
      href = `${window.location.origin}${errFile}`;
    } else {
      const res = await getPreReadSignedWriteUrl({ objectKey: errFile });
      if (res?.code !== 200 || !res?.data) return;
      href = res.data;
    }
    const link = document.createElement("a");
    link.href = href;
    link.setAttribute("download", errFile.split('/').pop() || errFile);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toggleErr();
  };

  // Add button functionality
  const handleAdd = () => {
    history.push('/todo-list/product-purchase-order-todo/create');
  };

  // Search debounce effect - only triggers when searchLoaded is true AND searchText actually changes
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchState?.searchLoaded && searchState?.searchText !== undefined && initialLoadCompleted.current) {
        setState((debouncestate) => ({
          ...debouncestate,
          currentPage: 1,
          totalItems: 0,
          hasMore: true,
          selectedIds: [],
          isSelectAll: false,
          expandAll: false,
        }));
        const params = {
          pageIndex: 1,
          pageSize: 20,
          searchText: searchState?.searchText?.trim(),
          filters: {
            supplierIds: filterState?.activeSupplier || [],
            colourwayIds: filterState?.activeColourways || [],
            statuses: filterState?.activeStatus || [],
          },
        };
        await fetchRef.current?.(params);
      }
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
  }, [searchState.searchText, searchState.searchLoaded]);

  useEffect(() => {
    const uploadFileToS3Async = async () => {
      if (actionState.filesSelected && actionState.importLoading) {
        try {
          const urlData = await dispatch(getPresignedUrlForPOUpload(actionState.filesSelected.fileName, t));
          if (urlData?.presignedUrl) {
            const uploadSuccess = await dispatch(uploadPOFileToS3(urlData.presignedUrl, actionState.filesSelected, urlData.uploadHeaders, t));
            if (uploadSuccess) {
              setActionState((prev) => ({
                ...prev,
                importLoading: false,
                filesSelected: null,
              }));
            } else {
              setActionState((prev) => ({
                ...prev,
                importLoading: false,
              }));
            }
          } else {
            setActionState((prev) => ({
              ...prev,
              importLoading: false,
            }));
          }
        } catch (error) {
          console.error('Error during file upload to S3:', error);
          setActionState((prev) => ({
            ...prev,
            importLoading: false,
          }));
        }
      }
    };

    uploadFileToS3Async();
  }, [actionState.filesSelected, actionState.importLoading, dispatch]);

  useEffect(() => {
    const loadInitialData = async () => {
      if (!initialLoadCompleted.current) {
        initialLoadCompleted.current = true;
        await fetchPurchaseOrderTodos({
          pageIndex: 1,
          pageSize: 20,
          searchText: '',
          filters: {
            supplierIds: [],
            colourwayIds: [],
            statuses: [],
          },
        });
        setSearchState((prev) => ({ ...prev, searchLoaded: true }));
      }
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    const handlePOListRefresh = async () => {
      if (initialLoadCompleted.current) {
        await fetchPurchaseOrderTodos({
          pageIndex: state.currentPage || 1,
          pageSize: state.itemsPerPage || 20,
          searchText: searchState.searchText || '',
          filters: {
            supplierIds: filterState?.activeSupplier || [],
            colourwayIds: filterState?.activeColourways || [],
            statuses: filterState?.activeStatus || [],
          },
        });
      }
    };

    window.addEventListener('po-list-refresh', handlePOListRefresh);

    return () => {
      window.removeEventListener('po-list-refresh', handlePOListRefresh);
    };
  }, [fetchPurchaseOrderTodos, state.currentPage, state.itemsPerPage, searchState.searchText, filterState]);

  if (state?.isInitialLoading && loading) {
    return <Loader />;
  }

  return (
    <>
      <ProductPurchaseOrderHeader
        columnDropdownOptions={columnDropdownOptions}
        selectedColumns={selectedColumns}
        handleSelectedColumns={handleSelectedColumns}
        searchState={searchState}
        handleSearch={handleSearch}
        handleClearSearch={handleClearSearch}
        state={state}
        allTierList={allTierList}
        ColorLibList={ColorLibList}
        filterState={filterState}
        setFilterState={setFilterState}
        handleApplyFilter={handleApplyFilter}
        expandSearchRef={expandSearchRef}
        handleIconClick={handleIconClick}
        handleRevisedColumns={handleRevisedColumns}
        handleActualColumns={handleActualColumns}
        totalCount={totalCount}
      />

      <div className="floting-actions">
        <OverlayTrigger
          placement="top"
          trigger="click"
          rootClose
          overlay={
            <Popover className="custom-popover file-download-popover">
              <Popover.Content>{renderDownloadPopover()}</Popover.Content>
            </Popover>
          }
        >
          <button type="button" className="btn" aria-label="Download">
            <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.DOWNLOAD')}>
              <i className="icon-Download" />
            </CustomTooltip>
          </button>
        </OverlayTrigger>
        {logic.POAddEditAccess && !isSupplierLoggedIn && (
          <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.UPLOAD')}>
            <button type="button" className="btn" aria-label={t('COMMON_LABEL.UPLOAD')} onClick={uploadXlsx}>
              <i className="icon-Upload-1 save-icon" />
            </button>
          </CustomTooltip>
        )}
        <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.ADD')}>
          <button type="button" className="btn" aria-label={t('COMMON_LABEL.ADD')} onClick={handleAdd}>
            <i className="icon-Add" />
          </button>
        </CustomTooltip>
      </div>
      <Container fluid>
        <ProductPurchaseOrderTable
          state={state}
          loading={loading}
          error={error}
          totalVisibleColumns={totalVisibleColumns}
          isColVisible={isColVisible}
          handleSelectAllPO={handleSelectAllPO}
          handleExpandAllFalsePO={handleExpandAllFalsePO}
          handleExpandAllTruePO={handleExpandAllTruePO}
          handleSelectPO={handleSelectPO}
          expandPO={expandPO}
          renderGroupHeaders={renderGroupHeaders}
          renderGroupCells={renderGroupCells}
          isRowEditable={isRowEditable}
          isColumnEditable={isColumnEditable}
          handleAutoSave={handleAutoSave}
          setState={setState}
          isSupplierLoggedIn={isSupplierLoggedIn}
          POAddEditAccess={logic.POAddEditAccess}
          PODeleteAccess={logic.PODeleteAccess}
          currencySymbol={currencySymbol}
          lengthVisible={lengthVisible}
          handlePageNextInScroll={handlePageNextInScroll}
          searchState={searchState}
          fetchPurchaseOrderTodos={fetchPurchaseOrderTodos}
        />
        {actionState.showUploadModal && (
          <ImportModal
            showUploadModal={actionState.showUploadModal}
            closeUploadModal={closeUploadModal}
            docRef={docRef}
            addDocument={handleFile}
            loader={actionState.importLoading}
          />
        )}
        {actionState.showErrModal && (
          <ErrorModal
            totalCount={actionState.totalRecordsToUpload}
            successCount={actionState.successRecordCount}
            errorCount={actionState.errorRecordCount}
            title={t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.UPLOAD_EXCEL')}
            onOpen={actionState.showErrModal}
            toggle={toggleErr}
            handleErrorDownload={handleErrorDownload}
          />
        )}
      </Container>
    </>
  );
}

export default ProductPurchaseOrderTodo;
